import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import {
  Bell, Clock3, Database, LayoutDashboard, LogOut, MessageSquare,
  Package, Plus, Settings, ShieldCheck, ShoppingCart, UserRoundCheck, Users,
} from 'lucide-react';
import { ScriptLibrary, FinancePage, TeamOverview, Recommendations } from './BusinessPages';
import { Admin } from './Admin';
import { Products, ProductNotifications } from './Products';
import { SettingsPage } from './SettingsPage';
import { Workbench, WorkRecordModal } from './Workbench';
import { CustomerManageModal, CustomerDeleteModal, CustomerExport, useCustomerAccess, canWriteCustomer, type CustomerPermission } from './CustomerManagement';
import { PoolImport } from './PoolImport';
import { useAppearance } from './appearance';
import { protectCustomerContacts } from './customerPrivacy';
import { CompletenessBadge, CompletenessPanel } from './CompletenessBadge';
import type { CustomerCompleteness } from './customerCompleteness';
import { customerBusinessChannels, normalizeCustomerSource } from './customerOptions';
import './style.css';
import './settings.css';

const businessDay = (value?: string | null) => value ? new Date(new Date(value).getTime() + 8 * 3600000).toISOString().slice(0, 10) : '';
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:4000' : '/'),
});
api.interceptors.request.use(config => {
  const token = localStorage.getItem('crm_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const nav = [
  ['dashboard', '工作台', LayoutDashboard],
  ['customers', '客户管理', Users],
  ['followups', '待跟进', Clock3],
  ['public', '公共客户池', Database],
  ['products', '产品中心', Package],
  ['notifications', '新品通知', Bell],
  ['recommendations', '新品推荐客户', Users],
  ['scripts', '共享话术', MessageSquare],
  ['finance', '订单收款', ShoppingCart],
  ['team', '团队总览', LayoutDashboard],
  ['admin', '管理后台', ShieldCheck],
  ['settings', '系统设置', Settings],
] as const;

type View = typeof nav[number][0];
type User = { jobTitle?: string | null; id: string; name: string; role: 'SUPER_ADMIN' | 'ADMIN' | 'SALESPERSON' };
type Order = {
  id: string;
  orderNo?: string | null;
  product?: string | null;
  amount?: string | number | null;
  expectedCloseAt?: string | null;
  completedAt?: string | null;
  note?: string | null;
  status: 'PENDING' | 'COMPLETED';
  cycleDays?: number | null;
  repurchaseDays?: number | null;
};
type Repurchase = { purchaseCount: number; totalAmount: string; lastCompletedAt: string | null; lastIntervalDays: number | null; sampleCount: number; typicalDays: number | null; predictedAt: string | null; reminderDays: number; overdueDays: number; due: boolean };
type Customer = {
  completeness?: CustomerCompleteness;
  contactMasked?: boolean;
  noteMasked?: boolean;
  nameMasked?: boolean;
  companyMasked?: boolean;
  sourceNoteMasked?: boolean;
  sourceMasked?: boolean;
  businessChannelsMasked?: boolean;
  permissions?: CustomerPermission;
  shopId?: string | null; shop?: { id: string; name: string } | null;
  sensitiveMasked?: boolean;
  id: string;
  name: string;
  company?: string | null;
  phone?: string | null;
  level: string;
  source?: string | null;
  sourceNote?: string | null;
  note?: string | null;
  owner?: { id?: string; name: string } | null;
  ownerId?: string | null;
  nextFollowupAt?: string | null;
  firstFollowupAt?: string | null;
  expectedCloseAt?: string | null; lastFollowupAt?: string | null;
  publicAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  wechat?: string | null;
  wangwang?: string | null;
  customerGroup?: string | null;
  businessChannel?: string | null;
  businessChannels?: string[] | null;
  activationStatus?: string | null;
  repurchaseReminderDays?: number;
  repurchase?: Repurchase;
  country?: string | null;
  city?: string | null;
  address?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  website?: string | null;
  reminderType?: 'FOLLOWUP' | 'ORDER_CLOSING_SOON' | 'REPURCHASE';
  reminderDate?: string;
  reminderOrder?: Order;
};
type CustomerDetail = Customer & {
  orders?: Order[];
  followups?: {
    id: string;
    user?: { name: string } | null;
    type: string;
    createdAt: string;
    content: string;
  }[];
};
type Summary = { total: number; due: number; publicCount: number; overdue: number };

const emptySummary: Summary = { total: 0, due: 0, publicCount: 0, overdue: 0 };
const levelNames: Record<string, string> = { VIP: 'VIP 客户', NORMAL: '一般客户', POTENTIAL: '潜在客户' };
const roleNames: Record<User['role'], string> = { SUPER_ADMIN: '超级管理员', ADMIN: '管理员', SALESPERSON: '业务员' };
const followupTypes: Record<string, string> = { MESSAGE: '消息（历史）', WECHAT: '微信', WANGWANG: '旺旺', CALL: '电话', VISIT: '拜访', EMAIL: '邮件', OTHER: '其他' };
const customerStatuses: Record<string, string> = { ACTIVE: '跟进中', PUBLIC: '公共客户', ARCHIVED: '已归档' };

function dateText(value?: string | null) {
  return value ? new Date(value).toLocaleDateString('zh-CN', { timeZone: 'Asia/Shanghai' }) : '—';
}

function errorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    return error.response?.data?.message || fallback;
  }
  return fallback;
}

function Modal({ title, close, busy = false, children, className = '' }: {
  title: string;
  close: () => void;
  busy?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const titleId = useId();
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousFocus = document.activeElement;
    container.current?.focus();
    return () => {
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
    };
  }, []);

  return (
    <div className="overlay">
      <div
        ref={container}
        className={`modal ${className}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={busy}
        tabIndex={-1}
        style={{ maxHeight: 'calc(100dvh - 24px)', overflowY: 'auto' }}
        onKeyDown={event => {
          if (event.key === 'Escape' && !busy) {
            event.stopPropagation();
            close();
          }
          if (event.key === 'Tab') {
            const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(
              'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href]',
            ));
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (first && event.shiftKey && (document.activeElement === first || document.activeElement === container.current)) {
              event.preventDefault();
              last.focus();
            } else if (first && !event.shiftKey && document.activeElement === last) {
              event.preventDefault();
              first.focus();
            }
          }
        }}
      >
        <div className="modal-title">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="x" aria-label="关闭弹窗" disabled={busy} onClick={close}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Login({ onLogin, notice }: { onLogin: (user: User) => void; notice: string }) {
  const [form, setForm] = useState({ username: 'admin', password: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post<{ token: string; user: User }>('/api/auth/login', form);
      localStorage.setItem('crm_token', data.token);
      onLogin(data.user);
    } catch (cause) {
      setError(errorMessage(cause, '登录失败，请检查账号、密码或网络后重试'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <form className="login-card" onSubmit={submit} aria-busy={busy}>
        <div className="brand-mark">奇</div>
        <h1>奇妙 CRM</h1>
        <p>客户关系管理平台</p>
        {notice && <p role="status">{notice}</p>}
        <input aria-label="登录账号" placeholder="登录账号" autoComplete="username" required value={form.username} onChange={event => setForm({ ...form, username: event.target.value })} />
        <input aria-label="密码" placeholder="密码" type="password" autoComplete="current-password" required value={form.password} onChange={event => setForm({ ...form, password: event.target.value })} />
        <button type="submit" disabled={busy || !form.username.trim() || !form.password}>{busy ? '登录中…' : '登录系统'}</button>
        {error && <small role="alert">{error}</small>}
      </form>
    </div>
  );
}

function FollowupModal({ customer, close, saved }: { customer: Customer; close: () => void; saved: () => void }) {
  const [content, setContent] = useState('');
  const [type, setType] = useState('MESSAGE');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy || !content.trim()) return;
    setBusy(true);
    setError('');
    try {
      await api.post(`/api/customers/${customer.id}/followups`, { content: content.trim(), type });
      saved();
      close();
    } catch (cause) {
      setError(errorMessage(cause, '保存跟进失败，请重试'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={`记录跟进 · ${customer.name}`} close={close} busy={busy}>
      <p className="muted">记录后将自动更新下次跟进日期，不会自动向客户发送消息。</p>
      <form onSubmit={submit}>
        <label className="field-label">跟进方式
          <select value={type} onChange={event => setType(event.target.value)}>
            {Object.entries(followupTypes).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
        <label className="field-label">跟进内容<textarea required rows={5} placeholder="请输入跟进内容" value={content} onChange={event => setContent(event.target.value)} /></label>
        {error && <p role="alert">{error}</p>}
        <div className="modal-actions">
          <button type="button" className="ghost" disabled={busy} onClick={close}>取消</button>
          <button type="submit" disabled={busy || !content.trim()}>{busy ? '保存中…' : '保存跟进'}</button>
        </div>
      </form>
    </Modal>
  );
}

function DetailModal({ id, revision, close, onFollow, onOrder, onChanged, canCorrectOrders, viewer, archived }: {
  viewer: User; archived: boolean;
  id: string;
  revision: number;
  close: () => void;
  onFollow: (customer: Customer) => void;
  onOrder: (customer: Customer) => void;
  onChanged: (message: string) => void;
  canCorrectOrders: boolean;
}) {
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [busyOrder, setBusyOrder] = useState<string | null>(null);
  const [completionDate, setCompletionDate] = useState(() => new Date(Date.now() + 8 * 3_600_000).toISOString().slice(0, 10));
  const [completionReason, setCompletionReason] = useState('');
  const today = new Date(Date.now() + 8 * 3_600_000).toISOString().slice(0, 10);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError('');
    void api.get<CustomerDetail>(`/api/customers/${id}${archived ? '?status=ARCHIVED' : ''}`, { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setCustomer(protectCustomerContacts(data, viewer.role)); })
      .catch(cause => { if (!controller.signal.aborted) setError(errorMessage(cause, '客户详情加载失败，请重试')); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [id, revision, retry, archived, viewer.role]);

  async function completeOrder(orderId: string) {
    if (busyOrder) return;
    setBusyOrder(orderId);
    setError('');
    try {
      if (!completionDate || completionDate > today || (completionDate < today && !completionReason.trim())) {
        setError('请选择实际成交日期；补录历史成交需填写原因。'); return;
      }
      await api.patch(`/api/orders/${orderId}/status`, { status: 'COMPLETED', completedAt: completionDate, ...(completionReason.trim() ? { correctionReason: completionReason.trim() } : {}) });
      onChanged('订单成交信息已保存');
    } catch (cause) {
      setError(errorMessage(cause, '更新订单失败，请重试'));
    } finally {
      setBusyOrder(null);
    }
  }

  const fields: [string, string][] = customer ? [
    ['公司', customer.company || '个人'],
    ['等级', levelNames[customer.level] || customer.level],
    ['负责人', customer.owner?.name || customer.ownerId || '待认领'],
    ['电话', customer.phone || '—'],
    ['客户来源', [normalizeCustomerSource(customer.source), normalizeCustomerSource(customer.source) === '其他渠道' ? customer.sourceNote : ''].filter(Boolean).join(' · ') || '—'],
    ['状态', customerStatuses[customer.status || ''] || customer.status || '—'],
    ['微信', customer.wechat || '—'],
    ['旺旺', customer.wangwang || '—'],
    ['客户分组', customer.customerGroup || '未分组'],
    ['主营渠道', customerBusinessChannels(customer).join(' · ') || '未设置'], ['客户归属店铺', customer.shop?.name || '未设置'],
    ['邮箱', customer.email || '—'],
    ['WhatsApp', customer.whatsapp || '—'],
    ['国家', customer.country || '—'],
    ['城市', customer.city || '—'],
    ['地址', customer.address || '—'],
    ['Facebook', customer.facebook || '—'],
    ['Instagram', customer.instagram || '—'],
    ['网站', customer.website || '—'],
    ['首次跟进', customer.firstFollowupAt ? dateText(customer.firstFollowupAt) : '未记录首次跟进'],
    ['最近跟进', dateText(customer.lastFollowupAt)],
    ['下次跟进', dateText(customer.nextFollowupAt)],
    ['创建日期', dateText(customer.createdAt)],
    ['更新日期', dateText(customer.updatedAt)],
    ...(customer.publicAt ? [['进入公海', dateText(customer.publicAt)] as [string, string]] : []),
  ] : [];

  return (
    <Modal title={customer?.name || '客户详情'} close={close} busy={Boolean(busyOrder)} className="detail-modal">
      {error && <p role="alert">{error} <button type="button" className="link-btn" disabled={loading || Boolean(busyOrder)} onClick={() => setRetry(current => current + 1)}>重新加载</button></p>}
      {loading ? <p role="status">加载中…</p> : customer && (
        <>
          <>{!canWriteCustomer(viewer, customer) && <p className="muted">{customer.status === 'ARCHIVED' ? '客户已归档，仅供查看。' : '此客户由负责人维护，你可以查看客户资料与跟进记录。'}</p>}{viewer.role !== 'SUPER_ADMIN' && <p className="muted">联系方式已脱敏，仅超级管理员可查看完整信息或修改。</p>}</>
          <div><b>客户资料</b><CompletenessBadge value={customer.completeness} /></div><CompletenessPanel value={customer.completeness} />
          <div className="detail-grid">
            {fields.map(([label, value]) => <div key={label} style={{ minWidth: 0, overflowWrap: 'anywhere' }}><small>{label}</small><b>{value}</b></div>)}
          </div>
          <p className="note" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{customer.note || '暂无特别备注'}</p>
          {customer.repurchase && <div className="note" aria-label="客户复购分析">
            <h3 style={{ margin: '0 0 8px' }}>成交与复购</h3>
            <p>最近成交：{dateText(customer.repurchase.lastCompletedAt)} · 累计成交额：¥{customer.repurchase.totalAmount}</p>
            <p>最近两次成交间隔：{customer.repurchase.lastIntervalDays == null ? '数据不足' : `${customer.repurchase.lastIntervalDays} 天`}</p>
            {customer.repurchase.predictedAt ? <p>预计复购：{dateText(customer.repurchase.predictedAt)} · 参考周期 {customer.repurchase.typicalDays} 天（最近 {customer.repurchase.sampleCount} 段间隔）· 提前 {customer.repurchase.reminderDays} 天提醒</p> : <p>至少需要 3 个不同日期的成交记录，才能估算复购周期。</p>}
            <small className="muted">同日拆单合并计算周期；预计日期供跟进参考。</small>
          </div>}
          <div className="section-heading">
            <h3><ShoppingCart size={16} aria-hidden="true" />订单</h3>
            <button type="button" className="small-btn" disabled={Boolean(busyOrder) || !canWriteCustomer(viewer, customer)} onClick={() => onOrder(customer)}><Plus size={14} aria-hidden="true" />录入订单</button>
          </div>
          {canWriteCustomer(viewer, customer) && (canCorrectOrders || customer.orders?.some(order => order.status === 'PENDING')) && <div className="form-grid" style={{ margin: '12px 0' }}>
            <label className="field-label">本次实际成交日期<input type="date" max={today} value={completionDate} onChange={event => setCompletionDate(event.target.value)} /></label>
            {(canCorrectOrders || completionDate < today) && <label className="field-label">更正或历史补录原因<input value={completionReason} maxLength={1000} onChange={event => setCompletionReason(event.target.value)} placeholder="更正已成交日期和历史补录时必填" /></label>}
          </div>}
          {customer.orders?.length ? (
            <div className="orders">
              {customer.orders.map(order => (
                <div className="order-row" key={order.id} style={{ flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <b>{order.orderNo || '未填写编号'} · {order.product || '未填写产品'}</b>
                    <small>预计成单：{order.expectedCloseAt ? dateText(order.expectedCloseAt) : '未设置'}{order.amount != null && ` · 金额 ${order.amount}`}</small>
                    {order.note && <small style={{ whiteSpace: 'pre-wrap' }}>{order.note}</small>}
                    {order.status === 'COMPLETED' && <small className="cycle">{order.cycleDays == null ? '未记录首次跟进' : `跟进至成交：${order.cycleDays} 天`}{order.repurchaseDays != null && ` · 距上次成交 ${order.repurchaseDays} 天`}</small>}
                    {order.completedAt && <small>完成日期：{dateText(order.completedAt)}</small>}
                  </div>
                  {order.status === 'COMPLETED' ? <div><span className="status">已完成</span>{canCorrectOrders && canWriteCustomer(viewer, customer) && <button type="button" className="link-btn" disabled={Boolean(busyOrder)} onClick={() => { if (!completionReason.trim()) { setError('请先填写更正原因，再更正成交日期。'); return; } void completeOrder(order.id); }}>更正成交日期</button>}</div> : (
                    <button type="button" className="link-btn" disabled={Boolean(busyOrder) || !canWriteCustomer(viewer, customer)} onClick={() => void completeOrder(order.id)}>{busyOrder === order.id ? '更新中…' : '标记已完成'}</button>
                  )}
                </div>
              ))}
            </div>
          ) : <span className="muted">暂无订单</span>}
          <h3>跟进历史</h3>
          <div className="history">
            {customer.followups?.length ? customer.followups.map(item => (
              <div key={item.id}>
                <b>{item.user?.name || '未知'} · {followupTypes[item.type] || item.type}</b>
                <small>{new Date(item.createdAt).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' })}</small>
                <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{item.content}</p>
              </div>
            )) : <span className="muted">暂无跟进记录</span>}
          </div>
          <div className="modal-actions">
            <button type="button" className="ghost" disabled={Boolean(busyOrder)} onClick={close}>关闭</button>
            <button type="button" disabled={Boolean(busyOrder) || !canWriteCustomer(viewer, customer)} onClick={() => { onFollow(customer); close(); }}><MessageSquare size={15} aria-hidden="true" />记录跟进</button>
          </div>
        </>
      )}
    </Modal>
  );
}

function UnreadBadge({ unread }: { unread: number | null }) {
  return (
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={unread == null ? '新品通知未读数量暂不可用' : `${unread} 条未读新品通知`}
      style={{ display: 'inline-flex', justifyContent: 'center', minWidth: 22, padding: '2px 6px', borderRadius: 12, background: unread ? '#b91c1c' : '#475569', color: '#fff', fontSize: 12 }}
    >
      {unread == null ? '—' : unread > 99 ? '99+' : unread}
    </span>
  );
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [loginNotice, setLoginNotice] = useState('');
  const [view, setView] = useState<View>('dashboard');
  const [rows, setRows] = useState<Customer[]>([]);
  const [summary, setSummary] = useState<Summary>(emptySummary);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [dataRevision, setDataRevision] = useState(0);
  const { access, accessError } = useCustomerAccess(api, user, dataRevision);
  const [managedCustomer, setManagedCustomer] = useState<Customer | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<Customer | null>(null);
  const [exportIds, setExportIds] = useState<string[] | null>(null);
  const [customerStatus, setCustomerStatus] = useState('ACTIVE');
  const [detailRevision, setDetailRevision] = useState(0);
  const [add, setAdd] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [follow, setFollow] = useState<Customer | null>(null);
  const [detail, setDetail] = useState<string | null>(null);
  const [order, setOrder] = useState<Customer | null>(null);
  const [workReleaseId, setWorkReleaseId] = useState('');
  const [filters, setFilters] = useState({ name: '', company: '', level: '', owner: '', lastFrom: '', lastTo: '', nextFrom: '', nextTo: '', expectedFrom: '', expectedTo: '' });
  const [work, setWork] = useState<Customer | 'new' | null>(null);
  const [claiming, setClaiming] = useState<string | null>(null);
  const [openingCustomer, setOpeningCustomer] = useState(false);
  const [unread, setUnread] = useState<number | null>(null);
  const [notificationError, setNotificationError] = useState('');
  const notificationRequest = useRef<AbortController | null>(null);
  const customerRequest = useRef<AbortController | null>(null);
  const claimRequest = useRef<AbortController | null>(null);
  const activeUserId = useRef<string | undefined>(undefined);
  const userId = user?.id;
  const appearance = useAppearance(userId);
  useEffect(() => {
    if (!userId) return;
    const interceptor = api.interceptors.response.use(response => response, error => {
      if (error.response?.status === 401) { logout(); setLoginNotice('登录已失效，请重新登录。'); }
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(interceptor);
  }, [userId]);
  const listView = view === 'dashboard' || view === 'customers' || view === 'followups' || view === 'public';

  useEffect(() => {
    activeUserId.current = userId;
    return () => { activeUserId.current = undefined; };
  }, [userId]);

  useEffect(() => {
    const controller = new AbortController();
    if (!localStorage.getItem('crm_token')) {
      setAuthChecked(true);
      return;
    }
    void api.get<{ user: User }>('/api/auth/me', { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setUser(data.user); })
      .catch(() => { if (!controller.signal.aborted) localStorage.removeItem('crm_token'); })
      .finally(() => { if (!controller.signal.aborted) setAuthChecked(true); });
    return () => controller.abort();
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (!userId || activeUserId.current !== userId || !localStorage.getItem('crm_token')) return;
    notificationRequest.current?.abort();
    const controller = new AbortController();
    notificationRequest.current = controller;
    try {
      // The default page also returns the total unread count, so no page parameter is needed.
      const { data } = await api.get<{ items: unknown[]; unread: number }>('/api/product-notifications', { signal: controller.signal });
      if (!controller.signal.aborted) {
        setUnread(data.unread);
        setNotificationError('');
      }
    } catch (cause) {
      if (!controller.signal.aborted) setNotificationError(errorMessage(cause, '新品通知数量更新失败，请重试'));
    }
  }, [userId]);

  const onPublished = useCallback(() => {
    if (!userId || activeUserId.current !== userId) return;
    setSuccess('产品发布成功');
    void refreshNotifications();
  }, [userId, refreshNotifications]);

  useEffect(() => {
    if (!userId) return;
    void refreshNotifications();
    const onFocus = () => { void refreshNotifications(); };
    const timer = window.setInterval(onFocus, 60_000);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
      notificationRequest.current?.abort();
    };
  }, [userId, refreshNotifications]);

  useEffect(() => () => {
    customerRequest.current?.abort();
    claimRequest.current?.abort();
  }, [userId]);

  useEffect(() => {
    if (!userId || !listView) return;
    const controller = new AbortController();
    const url = view === 'followups' ? '/api/followups/todo' : view === 'public' ? '/api/public-customers' : `/api/customers${view === 'customers' && customerStatus === 'ARCHIVED' ? '?status=ARCHIVED' : ''}`;
    setLoading(true);
    setLoadError('');
    setRows([]);
    void Promise.allSettled([
      api.get<Customer[]>(url, { signal: controller.signal }),
      api.get<Summary>('/api/dashboard/summary', { signal: controller.signal }),
    ]).then(([customerResult, summaryResult]) => {
      if (controller.signal.aborted) return;
      const errors: string[] = [];
      if (customerResult.status === 'fulfilled') setRows(customerResult.value.data.map(item => protectCustomerContacts(item, user?.role || 'SALESPERSON')));
      else errors.push(errorMessage(customerResult.reason, '客户列表加载失败'));
      if (summaryResult.status === 'fulfilled') setSummary(summaryResult.value.data);
      else errors.push(errorMessage(summaryResult.reason, '工作台统计加载失败'));
      setLoadError(errors.join('；'));
      setLoading(false);
    });
    return () => controller.abort();
  }, [userId, user?.role, view, listView, dataRevision, customerStatus]);

  function saved(message: string) {
    if (!userId || activeUserId.current !== userId) return;
    setSuccess(message);
    setActionError('');
    setDataRevision(current => current + 1);
    // A new order leaves the customer ID unchanged; explicitly invalidate its detail.
    setDetailRevision(current => current + 1);
    void refreshNotifications();
  }

  function navigate(nextView: View) {
    customerRequest.current?.abort();
    setOpeningCustomer(false);
    setSuccess('');
    setActionError('');
    setView(nextView);
  }

  function logout() {
    activeUserId.current = undefined;
    notificationRequest.current?.abort();
    customerRequest.current?.abort();
    claimRequest.current?.abort();
    localStorage.removeItem('crm_token');
    setUser(null);
    setView('dashboard');
    setRows([]);
    setSummary(emptySummary);
    setUnread(null);
    setNotificationError('');
    setLoadError('');
    setActionError('');
    setSuccess('');
    setAdd(false); setEditingCustomer(null);
    setWork(null); setWorkReleaseId(''); setManagedCustomer(null); setDeletingCustomer(null); setExportIds(null); setCustomerStatus('ACTIVE');
    setFollow(null);
    setDetail(null);
    setOrder(null);
    setClaiming(null);
    setOpeningCustomer(false);
  }

  async function claim(customer: Customer) {
    if (claiming) return;
    const controller = new AbortController();
    claimRequest.current = controller;
    setClaiming(customer.id);
    setActionError('');
    try {
      await api.post(`/api/public-customers/${customer.id}/claim`, undefined, { signal: controller.signal });
      if (!controller.signal.aborted) saved(`已认领客户「${customer.name}」`);
    } catch (cause) {
      if (!controller.signal.aborted) setActionError(errorMessage(cause, '认领客户失败，请重试'));
    } finally {
      if (!controller.signal.aborted) setClaiming(null);
    }
  }

  const openProductFollow = useCallback((customerId: string, releaseId = '') => {
    customerRequest.current?.abort();
    const controller = new AbortController();
    customerRequest.current = controller;
    setOpeningCustomer(true);
    setActionError('');
    void api.get<CustomerDetail>(`/api/customers/${customerId}`, { signal: controller.signal })
      .then(({ data }) => {
        if (controller.signal.aborted) return;
        setDetail(null);
        setOrder(null);
        setWorkReleaseId(releaseId); setWork(protectCustomerContacts(data, user?.role || 'SALESPERSON'));
      })
      .catch(cause => { if (!controller.signal.aborted) setActionError(errorMessage(cause, '无法加载该客户，请重试')); })
      .finally(() => { if (!controller.signal.aborted) setOpeningCustomer(false); });
  }, [user?.role]);

  if (!authChecked) return <div className="login"><p role="status">正在恢复登录…</p></div>;
  if (!user) return <Login notice={loginNotice} onLogin={next => { setLoginNotice(''); setUser(next); }} />;

  const filteredRows = rows.filter(customer => (!filters.name || customer.name.includes(filters.name)) && (!filters.company || (customer.company || '').includes(filters.company)) && (!filters.level || customer.level === filters.level) && (!filters.owner || (customer.ownerId || customer.owner?.id) === filters.owner) && (!filters.lastFrom || businessDay(customer.lastFollowupAt) >= filters.lastFrom) && (!filters.lastTo || Boolean(customer.lastFollowupAt) && businessDay(customer.lastFollowupAt) <= filters.lastTo) && (!filters.nextFrom || businessDay(customer.nextFollowupAt) >= filters.nextFrom) && (!filters.nextTo || Boolean(customer.nextFollowupAt) && businessDay(customer.nextFollowupAt) <= filters.nextTo) && (!filters.expectedFrom || businessDay(customer.expectedCloseAt || customer.reminderOrder?.expectedCloseAt) >= filters.expectedFrom) && (!filters.expectedTo || Boolean(customer.expectedCloseAt || customer.reminderOrder?.expectedCloseAt) && businessDay(customer.expectedCloseAt || customer.reminderOrder?.expectedCloseAt) <= filters.expectedTo));
  const title = nav.find(item => item[0] === view)?.[1] || '工作台';
  const descriptions: Record<View, string> = {
    dashboard: '欢迎回来，今天也要保持客户连接。',
    customers: '管理客户资料、跟进节奏与订单周期',
    followups: '按业务规则显示待跟进客户、即将成单及已到期未完成的订单。',
    public: '认领公共客户，继续维护客户关系。',
    products: '管理产品资料与新品发布。',
    notifications: '查看新品通知，选择客户后记录跟进；不会自动发送消息。',
    admin: '管理系统用户、权限与审计日志。',
    recommendations: '按客户偏好完成新品推广。', scripts: '积累和复用团队话术。', finance: '订单应收与实际收款。', team: '汇总团队工作、成交和收款。',
    settings: '管理个人偏好、账号安全与团队业务规则。',
  };

  return (
    <div className="shell">
      <aside aria-label="主导航">
        <div className="brand"><span>奇</span>奇妙 CRM</div>
        <small>工作台</small>
        <nav className="primary-nav" aria-label="页面导航">
          {nav.filter(([id]) => id === 'team' ? user.role !== 'SALESPERSON' || user.jobTitle === '组长' : id !== 'admin' || user.role !== 'SALESPERSON').map(([id, label, Icon]) => (
            <button
              type="button"
              className={`nav link-btn ${view === id ? 'active' : ''}`}
              aria-current={view === id ? 'page' : undefined}
              key={id}
              onClick={() => navigate(id)}
              style={{ color: view === id ? '#fff' : '#98a7bd', font: 'inherit', textAlign: 'left', flexShrink: 0 }}
            >
              <Icon size={18} aria-hidden="true" />{label}
              {id === 'notifications' && <UnreadBadge unread={unread} />}
            </button>
          ))}
        </nav>
        <div className="aside-bottom">
          <button type="button" className="nav link-btn" style={{ color: 'inherit', font: 'inherit' }} onClick={logout}><LogOut size={18} aria-hidden="true" />退出登录</button>
        </div>
      </aside>
      <main style={{ minWidth: 0 }}>
        <header>
          <div>工作台 / <b>{title}</b></div>
          <div className="user" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {user.name}<span>{roleNames[user.role]}</span>
            <button type="button" className="small-btn ghost" aria-label="退出登录" onClick={logout}><LogOut size={14} aria-hidden="true" />退出</button>
          </div>
        </header>
        <section>
          {view !== 'products' && view !== 'notifications' && (
            <div className="title-row">
              <div><h1>{title}</h1><p className="muted">{descriptions[view]}</p></div>
              {(view === 'customers' || view === 'dashboard') && <button type="button" className="primary" onClick={() => setAdd(true)}><Plus size={17} aria-hidden="true" />新建客户</button>}
            </div>
          )}
          {success && <p role="status">{success}</p>}
          {actionError && <p role="alert">{actionError}</p>}
          {openingCustomer && <p role="status">正在加载客户，准备记录跟进…</p>}
          {notificationError && <p role="alert">{notificationError} <button type="button" className="link-btn" onClick={() => void refreshNotifications()}>刷新通知</button></p>}
          {listView && loadError && <p role="alert">{loadError} <button type="button" className="link-btn" disabled={loading} onClick={() => setDataRevision(current => current + 1)}>重新加载</button></p>}
          {view === 'dashboard' && (
            <>
              <Workbench api={api} user={user} refreshKey={dataRevision} onOpenWorkRecord={() => setWork('new')} />
              <div className="stats" aria-busy={loading}>
                <div><small>我的客户</small><b>{summary.total}</b></div>
                <div><small>待跟进</small><b>{summary.due}</b></div>
                <div><small>已超时</small><b className="red">{summary.overdue}</b></div>
                <div><small>公共客户</small><b>{summary.publicCount}</b></div>
              </div>
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-head" style={{ alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <b style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Bell size={18} aria-hidden="true" />新品通知 <UnreadBadge unread={unread} /></b>
                  <button type="button" className="small-btn" onClick={() => navigate('notifications')}>查看新品通知</button>
                </div>
                <p className="muted" style={{ padding: '16px 20px', margin: 0 }}>
                  {unread == null ? (notificationError ? '未读数量暂不可用，可重试或进入通知列表查看。' : '正在获取未读通知数量…') : unread ? `有 ${unread} 条新品通知待阅读，查看后可选择客户记录跟进。` : '暂无未读新品通知，可查看历史通知。'}
                </p>
              </div>
            </>
          )}
          {view === 'scripts' && <ScriptLibrary api={api} user={user} />}
          {view === 'finance' && <FinancePage api={api} role={user.role} />}
          {view === 'team' && (user.role !== 'SALESPERSON' || user.jobTitle === '组长') && <TeamOverview api={api} />}
          {view === 'recommendations' && <Recommendations api={api} onFollow={(id, releaseId) => { void openProductFollow(id, releaseId); }} />}
          {view === 'admin' && <Admin api={api} user={user} onUserUpdated={setUser} />}
          {view === 'settings' && <SettingsPage api={api} appearance={appearance} onProfile={setUser} onPasswordChanged={() => { logout(); setLoginNotice('密码已修改，请使用新密码重新登录。'); }} />}
          {view === 'products' && <Products api={api} role={user.role} onPublished={onPublished} />}
          {view === 'notifications' && <ProductNotifications api={api} onRead={refreshNotifications} onFollow={openProductFollow} />}
          {listView && (
            <div className="card" aria-busy={loading}>
              <div className="card-head"><b>{view === 'followups' ? '待跟进客户' : view === 'public' ? '公共客户池' : access.title}</b><span>{filteredRows.length} 条 {view !== 'public' && view !== 'dashboard' && <button type="button" className="small-btn" onClick={() => setWork('new')}>记录工作</button>}</span></div>
              {accessError && <p role="alert" style={{ padding: 16 }}>{accessError}</p>}
              {view === 'customers' && access.canManage && <div className="biz-toolbar" style={{ padding: 16 }}><label>客户状态<select aria-label="客户状态" value={customerStatus} onChange={event => { setCustomerStatus(event.target.value); setFilters(current => ({ ...current, owner: '' })); }}><option value="ACTIVE">跟进中</option><option value="ARCHIVED">已归档</option></select></label>{user.role === 'SUPER_ADMIN' && access.canExport && customerStatus === 'ACTIVE' && <button className="ghost" type="button" disabled={!filteredRows.length || filteredRows.length > 500} onClick={() => setExportIds([...new Set(filteredRows.map(row => row.id))])}>导出筛选客户</button>}</div>}
              {view === 'public' && user.role !== 'SALESPERSON' && <PoolImport api={api} onImported={saved} />}
              <div className="biz-toolbar" style={{ padding: 16 }}><input aria-label="筛选客户名称" placeholder="客户名称" value={filters.name} onChange={event => setFilters({ ...filters, name: event.target.value })} /><input aria-label="筛选公司" placeholder="公司 / 个人" value={filters.company} onChange={event => setFilters({ ...filters, company: event.target.value })} /><select aria-label="筛选客户等级" value={filters.level} onChange={event => setFilters({ ...filters, level: event.target.value })}><option value="">全部等级</option><option value="VIP">VIP</option><option value="NORMAL">一般客户</option><option value="POTENTIAL">潜在客户</option></select>{view !== 'public' && <><>{access.canFilterOwner && <select aria-label="筛选负责人" value={filters.owner} onChange={event => setFilters({ ...filters, owner: event.target.value })}><option value="">全部负责人</option>{access.owners.map(owner => <option key={owner.id} value={owner.id}>{owner.name}{owner.department ? ` · ${owner.department.name}` : ''}</option>)}</select>}</><label>最近跟进从<input type="date" value={filters.lastFrom} onChange={event => setFilters({ ...filters, lastFrom: event.target.value })} /></label><label>至<input type="date" value={filters.lastTo} onChange={event => setFilters({ ...filters, lastTo: event.target.value })} /></label><label>下次跟进从<input type="date" value={filters.nextFrom} onChange={event => setFilters({ ...filters, nextFrom: event.target.value })} /></label><label>至<input type="date" value={filters.nextTo} onChange={event => setFilters({ ...filters, nextTo: event.target.value })} /></label><label>预计成交从<input type="date" value={filters.expectedFrom} onChange={event => setFilters({ ...filters, expectedFrom: event.target.value })} /></label><label>至<input type="date" value={filters.expectedTo} onChange={event => setFilters({ ...filters, expectedTo: event.target.value })} /></label></>}<button type="button" className="ghost" onClick={() => setFilters({ name: '', company: '', level: '', owner: '', lastFrom: '', lastTo: '', nextFrom: '', nextTo: '', expectedFrom: '', expectedTo: '' })}>重置</button></div><div className="table-wrap">
                <table>
                  <thead><tr><th scope="col">客户</th><th scope="col">公司</th><th scope="col">等级</th><th scope="col">负责人</th><th scope="col">最近跟进</th><th scope="col">下次跟进 / 预计成单</th><th scope="col">操作</th></tr></thead>
                  <tbody>
                    {filteredRows.map(customer => (
                      <tr key={`${customer.reminderType || 'customer'}-${customer.reminderOrder?.id || customer.id}`}>
                        <td><b>{customer.name}</b>{view !== 'public' && <CompletenessBadge value={customer.completeness} />}<small>{view === 'public' && (user.role !== 'SUPER_ADMIN' || customer.sensitiveMasked) ? '******' : customer.phone || '—'}</small>{view === 'public' && <details><summary>{user.role !== 'SUPER_ADMIN' || customer.sensitiveMasked ? '敏感资料已脱敏' : '查看联系资料'}</summary>{([['wechat', '微信'], ['wangwang', '旺旺'], ['email', '邮箱'], ['whatsapp', 'WhatsApp'], ['facebook', 'Facebook'], ['instagram', 'Instagram'], ['website', '网站'], ['address', '地址'], ['note', '备注']] as const).map(([key, label]) => <small key={key}>{label}：{user.role !== 'SUPER_ADMIN' || customer.sensitiveMasked ? '******' : customer[key] || '—'}</small>)}</details>}</td>
                        <td>{customer.company || '个人'}{view !== 'public' && customer.shop && <small>归属店铺：{customer.shop.name}</small>}{view !== 'public' && <small>{[customer.customerGroup, ...customerBusinessChannels(customer)].filter(Boolean).join(' · ')}</small>}</td>
                        <td><em>{levelNames[customer.level] || customer.level}</em></td>
                        <td>{customer.owner?.name || customer.ownerId || '待认领'}</td>
                        <td>{view === 'public' ? '领取后查看' : dateText(customer.lastFollowupAt)}</td><td>
                          {customer.reminderType === 'ORDER_CLOSING_SOON' ? dateText(customer.reminderOrder?.expectedCloseAt) : customer.reminderType === 'REPURCHASE' ? dateText(customer.reminderDate) : dateText(customer.nextFollowupAt)}
                          {customer.reminderType === 'REPURCHASE' && <small className="cycle">预计复购 · {customer.repurchase?.typicalDays} 天周期{Boolean(customer.repurchase?.overdueDays) && ` · 已超预计 ${customer.repurchase?.overdueDays} 天`}</small>}
                          {view !== 'public' && customer.expectedCloseAt && <small>预计成交 {dateText(customer.expectedCloseAt)}</small>}
                          {view !== 'public' && customer.reminderType !== 'REPURCHASE' && customer.repurchase?.predictedAt && <small>预计复购 {dateText(customer.repurchase.predictedAt)}</small>}
                          {customer.reminderType === 'ORDER_CLOSING_SOON' && <small className="cycle">成单前提醒 · {customer.reminderOrder?.orderNo || customer.reminderOrder?.product || '订单'}</small>}
                        </td>
                        <td>
                          {view === 'public' ? (
                            <button type="button" className="link-btn" disabled={Boolean(claiming)} aria-label={`认领客户 ${customer.name}`} onClick={() => void claim(customer)}><UserRoundCheck size={15} aria-hidden="true" />{claiming === customer.id ? '认领中…' : '认领'}</button>
                          ) : (
                            <>
                              <button type="button" className="link-btn" aria-label={`查看客户 ${customer.name} 详情`} onClick={() => setDetail(customer.id)}>详情</button>
                              {view === 'customers' && customer.status === 'ACTIVE' && canWriteCustomer(user, customer) && <button type="button" className="link-btn" aria-label={`编辑客户 ${customer.name}`} onClick={() => setEditingCustomer(customer)}>编辑</button>}
                              {canWriteCustomer(user, customer) && <button type="button" className="link-btn" aria-label={`跟进客户 ${customer.name}`} onClick={() => setWork(customer)}><MessageSquare size={15} aria-hidden="true" />跟进</button>}
                            </>
                          )}
                          {access.canManage && <button type="button" className="link-btn" aria-label={`管理客户 ${customer.name}`} onClick={() => setManagedCustomer(customer)}>分配／管理</button>}
                          {view === 'customers' && access.canManage && user.role !== 'SALESPERSON' && <button type="button" className="link-btn customer-delete-link" aria-label={`删除客户 ${customer.name}`} onClick={() => setDeletingCustomer(customer)}>删除</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!filteredRows.length && <div className="empty">{loading ? '加载中…' : loadError ? '数据暂不可用，请重试' : '暂无数据'}</div>}
              </div>
            </div>
          )}
          {managedCustomer && <CustomerManageModal api={api} customer={managedCustomer} access={access} close={() => setManagedCustomer(null)} saved={saved} />}
          {deletingCustomer && access.canManage && user.role !== 'SALESPERSON' && <CustomerDeleteModal key={deletingCustomer.id} api={api} customer={deletingCustomer} close={() => setDeletingCustomer(null)} saved={message => { setRows(current => current.filter(row => row.id !== deletingCustomer.id)); if (detail === deletingCustomer.id) setDetail(null); if (editingCustomer?.id === deletingCustomer.id) setEditingCustomer(null); if (managedCustomer?.id === deletingCustomer.id) setManagedCustomer(null); if (work !== 'new' && work?.id === deletingCustomer.id) setWork(null); if (follow?.id === deletingCustomer.id) setFollow(null); if (order?.id === deletingCustomer.id) setOrder(null); saved(message); }} />}
          {exportIds && user.role === 'SUPER_ADMIN' && <CustomerExport api={api} ids={exportIds} close={() => setExportIds(null)} saved={saved} />}
          {add && <WorkRecordModal api={api} user={user} initialMode="customer" close={() => setAdd(false)} saved={() => saved('客户已保存')} />}
          {editingCustomer && canWriteCustomer(user, editingCustomer) && <WorkRecordModal key={`edit-${editingCustomer.id}`} api={api} user={user} customer={editingCustomer} initialMode="customer" close={() => setEditingCustomer(null)} saved={() => saved('客户资料已更新')} />}
          {work && <WorkRecordModal key={work === 'new' ? 'work-new' : work.id} api={api} user={user} customer={work === 'new' ? undefined : work} initialReleaseId={workReleaseId} close={() => { setWork(null); setWorkReleaseId(''); }} saved={() => saved('工作记录已保存，日报与每日事项已更新')} />}
          {follow && <FollowupModal key={`follow-${follow.id}`} customer={follow} close={() => setFollow(null)} saved={() => saved('跟进记录已保存')} />}
          {detail && <DetailModal key={`detail-${detail}`} id={detail} revision={detailRevision} close={() => setDetail(null)} onFollow={setWork} onOrder={setOrder} onChanged={saved} canCorrectOrders={user.role !== 'SALESPERSON'} viewer={user} archived={customerStatus === 'ARCHIVED' && view === 'customers'} />}
          {order && <WorkRecordModal key={`order-${order.id}`} api={api} user={user} customer={order} initialMode="order" close={() => setOrder(null)} saved={() => { setDetail(order.id); saved('客户资料与订单已保存'); }} />}
        </section>
      </main>
    </div>
  );
}

// Reuse the root when Vite refreshes this entry module during development.
const root: ReturnType<typeof createRoot> = import.meta.hot?.data.root ?? createRoot(document.getElementById('root')!);
if (import.meta.hot) import.meta.hot.data.root = root;
root.render(<App />);
